import type {
    TiktokCommercialContent,
    TiktokCreatorInfo,
    TiktokPrivacyLevel,
    TiktokPublishConsentType,
} from '../types/tiktokDirectPost.types';
import type { TiktokPostFields } from '../components/InstagramAutomation/platformPostFields';

export const TIKTOK_PRIVACY_LABELS: Record<TiktokPrivacyLevel, string> = {
    PUBLIC_TO_EVERYONE: 'Everyone',
    MUTUAL_FOLLOW_FRIENDS: 'Mutual followers',
    FOLLOWER_OF_CREATOR: 'Followers',
    SELF_ONLY: 'Only me',
};

export function resolveTiktokPublishConsentType(commercial: TiktokCommercialContent): TiktokPublishConsentType {
    if (!commercial.disclosure_enabled) return 'music_usage_only';
    if (commercial.your_brand && commercial.branded_content) {
        return 'your_brand_and_branded_content_and_music';
    }
    if (commercial.branded_content) return 'branded_content_and_music';
    return 'music_usage_only';
}

export function getTiktokPublishDeclaration(consentType: TiktokPublishConsentType): string {
    if (consentType === 'branded_content_and_music') {
        return "By posting, you agree to TikTok's Branded Content Policy and Music Usage Confirmation.";
    }
    if (consentType === 'your_brand_and_branded_content_and_music') {
        return "By posting, you agree to TikTok's Branded Content Policy and Music Usage Confirmation.";
    }
    return "By posting, you agree to TikTok's Music Usage Confirmation.";
}

export function getTiktokCommercialLabelPrompt(commercial: TiktokCommercialContent): string | null {
    if (!commercial.disclosure_enabled) return null;
    if (commercial.your_brand && commercial.branded_content) {
        return "Your video will be labeled as 'Paid partnership'";
    }
    if (commercial.branded_content) {
        return "Your video will be labeled as 'Paid partnership'";
    }
    if (commercial.your_brand) {
        return "Your video will be labeled as 'Promotional content'";
    }
    return null;
}

export interface TiktokPostValidationResult {
    valid: boolean;
    message?: string;
}

export function validateTiktokPostFields(
    fields: TiktokPostFields,
    creatorInfo?: TiktokCreatorInfo | null,
): TiktokPostValidationResult {
    if (creatorInfo && !creatorInfo.can_post_now) {
        return {
            valid: false,
            message:
                creatorInfo.blocked_reason_message ?? 'You cannot post to TikTok right now. Please try again later.',
        };
    }

    if (creatorInfo?.duration_allowed === false) {
        return {
            valid: false,
            message: `Video exceeds TikTok's maximum length (${creatorInfo.max_video_post_duration_sec}s).`,
        };
    }

    if (!fields.tiktok_privacy_level) {
        return { valid: false, message: 'Select who can view your TikTok post.' };
    }

    if (
        creatorInfo?.privacy_level_options?.length &&
        !creatorInfo.privacy_level_options.includes(fields.tiktok_privacy_level)
    ) {
        return { valid: false, message: 'Selected privacy option is not available for your TikTok account.' };
    }

    const commercial = fields.tiktok_commercial_content;
    if (commercial.disclosure_enabled && !commercial.your_brand && !commercial.branded_content) {
        return {
            valid: false,
            message: 'Indicate if your content promotes yourself, a third party, or both.',
        };
    }

    if (commercial.disclosure_enabled && commercial.branded_content && fields.tiktok_privacy_level === 'SELF_ONLY') {
        return {
            valid: false,
            message: 'Branded content cannot be private. Choose a public or friends visibility.',
        };
    }

    if (!fields.tiktok_publish_consent_accepted) {
        return { valid: false, message: 'Accept the TikTok posting declaration to continue.' };
    }

    return { valid: true };
}

export function isTiktokPrivacyDisabledForBranded(
    privacy: TiktokPrivacyLevel,
    commercial: TiktokCommercialContent,
): boolean {
    return commercial.disclosure_enabled && commercial.branded_content && privacy === 'SELF_ONLY';
}
